import FormField from 'components/FormField';
import { FV, getDefaultTemplate } from './share';
import { Button, Checkbox, Divider, Input, message, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { getGroupList, getWabaList } from '../service';
import { langRegionMap, regionLangMap, useGetRegionLabel } from '../share';
import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import useModal from 'hooks/useModal';
import GroupForm from '../GroupForm';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';
import { Element } from 'react-scroll';

function Basic({
  mode,
  templateList,
}: {
  mode: string;
  templateList: Aide.WhatsAppTemplateGroup[];
}) {
  const { t, getRegionLabel } = useGetRegionLabel();

  const { values, setValues } = useFormikContext<FV>();

  const { data: wabaList, loading: wabaLoading } = useRequest(
    () => {
      return getWabaList().then(({ accounts }) => accounts);
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const {
    data: groupList,
    loading: groupLoading,
    refreshAsync: refreshGroupList,
  } = useRequest(
    () => {
      return getGroupList();
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const wabaOptions = useMemo(() => {
    return wabaList?.map(({ wabaId, phone }) => {
      return {
        value: wabaId,
        label: phone,
      };
    });
  }, [wabaList]);

  const groupOptions = useMemo(() => {
    return groupList?.map((group) => {
      return {
        value: group,
        label: group,
      };
    });
  }, [groupList]);

  const groupFormModalState = useModal();

  const groupFormModal = (
    <GroupForm
      visible={groupFormModalState.visible}
      onCancel={groupFormModalState.hide}
      groupList={groupList || []}
      onSave={(group) => {
        setValues({
          ...values,
          group,
        });
        refreshGroupList();
      }}
    />
  );

  const { basicId } = useReactScrollContext();

  return (
    <Element name={basicId}>
      <h4>{t('aide.基础配置')}</h4>
      <FormField<FV['wabaId']>
        name="wabaId"
        required
        label={t('WABA账号')}
        validate={(value) => (value ? undefined : t('department.请选择x', { x: t('WABA账号') }))}
      >
        {({ field }) => {
          return (
            <Select
              loading={wabaLoading}
              placeholder={t('department.请选择x', { x: t('WABA账号') })}
              options={wabaOptions}
              {...field}
            />
          );
        }}
      </FormField>
      <FormField<FV['category']>
        name="category"
        required
        label={t('类别')}
        description={({ field: { value } }) =>
          value === 'MARKETING'
            ? t(
                '与企业/产品/服务相关。可能包含促销或优惠信息；欢迎消息或结束语；动态更新、邀请或推荐；对于回复或完成新交易的请求。\n只要包含营销内容，都归类为营销模板。',
              )
            : value === 'UTILITY'
            ? t('与已达成的具体交易相关，用于完成以下事项：确认、中止或更改交易。')
            : undefined
        }
        validate={(value) => (value ? undefined : t('department.请选择x', { x: t('类别') }))}
      >
        {({ field }) => {
          return (
            <Select
              placeholder={t('department.请选择x', { x: t('类别') })}
              options={[
                { value: 'MARKETING', label: t('市场营销') },
                { value: 'UTILITY', label: t('交易相关') },
              ]}
              {...field}
            />
          );
        }}
      </FormField>
      <FormField<FV['group']>
        name="group"
        required
        label={
          <div>
            <span>{t('phrase.分组')}</span>
            <Button type="link" icon={<PlusOutlined />} onClick={groupFormModalState.show}>
              {t('phrase.添加分组')}
            </Button>
            {groupFormModal}
          </div>
        }
        validate={(value) => (value ? undefined : t('department.请选择x', { x: t('phrase.分组') }))}
      >
        {({ field }) => {
          return (
            <Select
              loading={groupLoading}
              placeholder={t('department.请选择x', { x: t('phrase.分组') })}
              options={groupOptions}
              {...field}
            />
          );
        }}
      </FormField>
      <FormField<FV['name']>
        name="name"
        required
        label={t('模版名称')}
        validate={(value) => {
          if (!value?.trim()) {
            return t('department.请输入x', { x: t('模版名称') });
          }
          if (!/^[a-z\d_]+$/.test(value)) {
            return t('模板名称仅支持使用小写字母、数字和下划线');
          }
          if (
            templateList.some((template) => {
              return (
                template.name === value &&
                (mode !== 'edit' ||
                  template.template.every((item) => {
                    return item.id !== values.regionTemplateMap[langRegionMap[item.lang]]?.id;
                  }))
              );
            })
          ) {
            return t('x已存在', { x: t('模板名称') });
          }
        }}
      >
        {({ field }) => {
          return (
            <Input
              showCount
              maxLength={100}
              placeholder={t('department.请输入x', { x: t('模版名称') })}
              {...field}
            />
          );
        }}
      </FormField>
      <FormField<FV['regionTemplateMap']>
        name="regionTemplateMap"
        required
        label={t('phrase.选择站点')}
        validate={(value) =>
          Object.values(value).some(({ cancelled }) => !cancelled)
            ? undefined
            : t('phrase.请选择站点')
        }
      >
        {({ field: { value, onChange, onBlur } }) => {
          const regionList = Object.keys(regionLangMap);
          const regionChecked = (region: Aide.WhatsAppRegion) =>
            value[region] && !value[region]?.cancelled;
          const allChecked = regionList.every(regionChecked);
          const indeterminate = !allChecked && regionList.some(regionChecked);
          return (
            <div>
              <Checkbox
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={({ target: { checked } }) => {
                  onBlur();
                  onChange(
                    regionList.reduce(
                      (res, region) => {
                        const cur = res[region];
                        if (checked) {
                          res[region] = cur
                            ? {
                                ...cur,
                                cancelled: false,
                              }
                            : getDefaultTemplate(regionLangMap[region]);
                        } else {
                          res[region] = cur
                            ? {
                                ...cur,
                                cancelled: true,
                              }
                            : undefined;
                        }
                        return res;
                      },
                      { ...value },
                    ),
                  );
                }}
              >
                {t('全部')}
              </Checkbox>
              <Divider type="vertical" />
              {regionList.map((region) => {
                return (
                  <Checkbox
                    checked={regionChecked(region)}
                    onChange={({ target: { checked } }) => {
                      onBlur();
                      const cur = value[region];
                      onChange({
                        ...value,
                        [region]: cur
                          ? {
                              ...cur,
                              cancelled: !checked,
                            }
                          : checked
                          ? getDefaultTemplate(regionLangMap[region])
                          : undefined,
                      });
                    }}
                  >
                    {getRegionLabel(region)}
                  </Checkbox>
                );
              })}
            </div>
          );
        }}
      </FormField>
    </Element>
  );
}

export default Basic;
